import { useMutation } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'

import { ordersCreateOrderShippingFeeRefundMutation } from '@repo/sdk'

import { extractUserFacingMessage } from '../../providers/sdkErrorUtils'

import { useInvalidateFulfillmentOrders } from './useInvalidateFulfillmentOrders'

interface ShippingFeeRefundParams {
  orderId: string
  reason?: string
}

export function useShippingFeeRefund(onSuccess: () => void) {
  const invalidateFulfillmentOrders = useInvalidateFulfillmentOrders()
  const { message } = App.useApp()
  const { t } = useTranslation()

  const { mutate, isPending } = useMutation({
    ...ordersCreateOrderShippingFeeRefundMutation(),
    onSuccess: async () => {
      message.success(t('common.modals.shippingFeeRefundSuccess'))
      // ['order-list'] is not a refine key — it never matched, so the views stayed stale.
      await invalidateFulfillmentOrders()
      onSuccess()
    },
    onError: (error) => {
      // The server explains why it refused — "환불할 배송비가 없습니다",
      // "이미 환불된 배송비입니다", "해당 주문에 대한 배송비 환불 권한이 없습니다".
      // Showing only the generic failure left the dealer retrying a button that can never
      // succeed. Mobile already distinguishes these; this screen did not.
      message.error(extractUserFacingMessage(error) ?? t('common.modals.shippingFeeRefundFailed'))
    },
  })

  const shippingFeeRefund = ({ orderId, reason }: ShippingFeeRefundParams) => {
    mutate({
      path: { orderId },
      body: reason ? { reason } : {},
    })
  }

  return { shippingFeeRefund, isRefunding: isPending }
}
